import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
  Switch,
} from 'react-router-dom';
import { connectHits } from 'react-instantsearch-dom';
import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faTrashAlt,
  faSort,
} from '@fortawesome/free-solid-svg-icons';
import {
  ALGOLIA_BREED_INDEX,
  BREEDS_ADMIN_PREFIX,
  BREEDS_BROWSER_API_PREFIX,
} from '../../../utils/constants';
import DateHelper from '../../../utils/DateHelper';
import AdminURLSync from '../AdminURLSync';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import { cloudinaryTransform } from '../../../utils/cloudinaryTransformations';
import EditSVG from './assets/EditSVG';
import { BreedTableInterface, SearchState } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

const Hit: React.FC<BreedTableInterface> = ({
  id,
  slug,
  name,
  thumbnailUrl,
  route,
  description,
  updated_at,
  selected,
  toggleSelected,
  canDelete,
  canEdit,
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const filterValues = useCallback((values) => {
    if (canDelete && canEdit) {
      return values;
    }
    const newValues = canDelete
      ? [...values]
      : [...values.filter((val: { key: string }) => val?.key !== 'delete' && val.key !== 'delete-mobile')];

    return canEdit
      ? newValues
      : [...newValues.filter((val: { key: string }) => val?.key !== 'edit' && val.key !== 'edit-mobile')];
  }, [canDelete, canEdit]);

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this breed: ${name}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${BREEDS_BROWSER_API_PREFIX}/${id}/bulk-destroy`)
                .then(({ data }) => {
                  res(`Breed (${name}) successfully deleted.`);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${BREEDS_ADMIN_PREFIX}/${slug}/edit`}
          aria-label={`Edit breed with name: ${name}.`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete breed with name: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'title',
      value: (
        <a
          className="d-flex align-items-center"
          href={route}
        >
          <img
            className="puppy-hit mr-1"
            alt={`${name} breed.`}
            src={cloudinaryTransform(
              thumbnailUrl,
              [
                'c_fill',
                'h_26,w_26',
                'f_auto,q_auto',
              ],
            )}
          />
          <p className="m-0 text-dark-btn-txt font-weight-bold">{name}</p>
        </a>
      ),
    },
    {
      key: 'description',
      value: (
        description ? (
          <p className="text-truncate notes text-table-light-gray">{description}</p>
        ) : <></>
      ),
    },
    {
      key: 'updated_at',
      value: updated_at
        ? (
          <p className="text-table-light-gray m-0">
            {DateHelper.getLocalizedDateTimeFromTimestamp(updated_at).format('MM/DD/YY H:mm:ss')}
          </p>
        ) : <></>,
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center justify-content-center"
          href={`${BREEDS_ADMIN_PREFIX}/${slug}/edit`}
          aria-label={`Edit breed with name: ${name}.`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete breed with name: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={filterValues(values)}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          title="Confirm Action"
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  canDelete?: boolean,
  canEdit?: boolean,
}> = ({
  hits,
  selected,
  toggleSelected,
  canDelete,
  canEdit,
}) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
      canDelete={canDelete}
      canEdit={canEdit}
    />
  );
};

const CustomHits = connectHits(Hits);

interface Props extends RouteComponentProps {
  canDelete: boolean,
  canEdit: boolean,
}

const AdminDashboardTableBreeds: React.FC<Props> = ({
  history,
  location,
  match,
  canDelete,
  canEdit,
}): ReactElement => {
  const [externalSearchState, setExternalSearchState] = useState<SearchState>({
    configure: {
      numericFilters: [],
      facetFilters: [],
    },
    query: '',
  });
  const [numberOfEmpties] = useState(() => {
    let numberEmpties = 0;
    if (canDelete) {
      numberEmpties += 1;
    }
    if (canEdit) {
      numberEmpties += 1;
    }
    return numberEmpties;
  });
  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const [isPopular, setIsPopular] = useState(false);
  const isPopularCallback = useCallback((checked: boolean) => {
    if (checked && !isPopular) {
      setIsPopular(true);
    } else if (!checked && isPopular) {
      setIsPopular(false);
    }
  }, [isPopular]);

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No breeds have been selected. Please, select one or more breeds.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} breeds?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${BREEDS_BROWSER_API_PREFIX}/${bulkSelected.join(',')}/bulk-destroy`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  const tableHeaderRowValues = [
    {
      key: 'name',
      value: 'Breed',
    },
    {
      key: 'description',
      value: 'Description',
    },
    {
      key: 'updated_at',
      value: 'Updated At:',
    },
  ];

  // TODO: Had to memoize this, because it was being re-rendered constantly; no idea why
  const ActionButton = useCallback(() => (
    <>
      <a
        className="mr-2 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt"
        href={`${BREEDS_ADMIN_PREFIX}/create`}
      >
        <span className="mr-2 action-font text-uppercase">
          Create New Breed
        </span>
        <FontAwesomeIcon icon={faPlay} size="xs" />
      </a>
      <a
        className="mr-2 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt"
        href={`${BREEDS_ADMIN_PREFIX}/popular`}
      >
        <span className="mr-2 action-font text-uppercase">
          Sort Popular Breeds
        </span>
        <FontAwesomeIcon icon={faSort} size="xs" />
      </a>
    </>
  ), []);

  const bulkActions = canDelete
    ? [{ label: 'Delete', value: 'delete' }]
    : [];

  return (
    <>
      <AdminURLSync
        title="Breeds"
        ActionButton={ActionButton}
        numEmptyStartHeaders={numberOfEmpties}
        numEmptyEndHeaders={numberOfEmpties}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={bulkActions}
        externalQueryParams={[]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={ALGOLIA_BREED_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
            canDelete={canDelete}
            canEdit={canEdit}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'updated_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay title="Updated:" />,
          },
          {
            type: 'facet_switch',
            attribute: 'is_popular',
            label: 'Popular Breeds Only',
            toggleCallback: isPopularCallback,
          },
        ]}
        dateAttributes={[]}
        tabs={[]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminDashboardTableBreeds;

if (document.getElementById('react-admin-dashboard-table-breeds')) {
  const element = document.getElementById('react-admin-dashboard-table-breeds');

  const canDelete = element?.dataset.candelete
    ? element?.dataset.candelete === '1'
    : false;

  const canEdit = element?.dataset.canedit
    ? element?.dataset.canedit === '1'
    : false;

  ReactDOM.render(
    <BrowserRouter>
      <Switch>
        {/* <Route */}
        {/*  path={`${ADS_ADMIN_PREFIX}/:type/:ids/bulk-edit`} */}
        {/*  render={(routeProps) => ( */}
        {/*    <AdsBulkEdit */}
        {/*      canSetExpiration={canSetExpiration} */}
        {/*      canDelete={canDelete} */}
        {/*      {...routeProps} */}
        {/*    /> */}
        {/*  )} */}
        {/* /> */}
        <Route
          path={BREEDS_ADMIN_PREFIX}
          render={(routeProps) => (
            <AdminDashboardTableBreeds
              canEdit={canEdit}
              canDelete={canDelete}
              {...routeProps}
            />
          )}
        />
      </Switch>
    </BrowserRouter>,
    element,
  );
}
